import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import { CsvBuilder } from "filefy";

import { TextItem } from "../../components/TextItem";
import GroupAirQualityMap, { useGroupAirQualityAggregation } from "../../components/environment/GroupAirQualityMap";
import GroupAirQualityCsvLegend from "../../components/environment/GroupAirQualityCsvLegend";
import GroupAirQualityMapHelp from "../../components/environment/GroupAirQualityMapHelp";
import TimeField from "../../components/environment/TimeField";
import { getErrorMessage } from "../../services/Client";
import { useGetGroupAirQualityDays, useGetGroupAirQualitySession } from "../../services/ContentManager";
import {
    GROUP_AIR_QUALITY_CSV_COLUMNS,
    buildGroupAirQualityCsvRows,
    groupAirQualityCsvFileName,
} from "../../services/groupAirQualityCsv";

const useStyles = makeStyles(theme => ({
    page: {
        padding: theme.spacing(2),
    },
    container: {
        marginBottom: theme.spacing(2),
    },
}));

const DEFAULT_FROM = "00:00";
const DEFAULT_TO = "23:59";

/** Errore da mostrare al posto della mappa, dal più generale al più specifico; null se non ce n'è. */
function pageErrorMessage(t, { daysError, daysStatus, days, error, data, participants, availableMetrics }) {
    if (daysError) return getErrorMessage(daysError);
    if (daysStatus === "success" && days.length === 0)
        return t("groupAirQualityNoDays", "Nessuna sessione con dati ambientali in questa iniziativa.");
    if (error) return getErrorMessage(error);
    if (!data) return null;
    if (participants.length === 0)
        return t("groupAirQualityNoSessionsInWindow", "Nessuna sessione con dati ambientali nella finestra oraria scelta.");
    if (!(data.pieces && data.pieces.length > 0))
        return t("groupAirQualityNoTrack", "Nessuna sessione della finestra ha insieme dati ambientali e un tracciato GPS: non c'è un percorso su cui disegnare.");
    if (availableMetrics.length === 0)
        return t("groupAirQualityNoValidSamples", "Le sessioni della finestra non hanno campioni validi per nessuna grandezza.");
    return null;
}

export default function GroupAirQuality() {
    const classes = useStyles();
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();

    const [legendOpen, setLegendOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const day = query.get("day") || "";
    const from = query.get("from") || DEFAULT_FROM;
    const to = query.get("to") || DEFAULT_TO;

    const setQuery = next => {
        const params = new URLSearchParams(location.search);
        Object.entries(next).forEach(([k, v]) => {
            if (v == null || v === "") params.delete(k);
            else params.set(k, v);
        });
        history.replace({ pathname: location.pathname, search: "?" + params.toString() });
    };

    const { days, status: daysStatus, error: daysError } = useGetGroupAirQualityDays(id);

    // Senza giorno in URL si parte dal più recente disponibile.
    useEffect(() => {
        if (!day && days.length > 0) setQuery({ day: days[0].day });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [day, days]);

    // La risposta ha gia' il percorso di gruppo, le soste e le posizioni stimate dei buchi GPS.
    const { data, status, error } = useGetGroupAirQualitySession(id, day, from, to);

    const aggregation = useGroupAirQualityAggregation(data);
    const { availableMetrics, metricLabel, result, results, tooltipText } = aggregation;

    const participants = useMemo(() => data?.participants || [], [data?.participants]);
    const included = participants.filter(p => p.status === "INCLUDED");
    const dayInfo = days.find(d => d.day === day);
    const usernameBySession = useMemo(() => {
        const out = {};
        participants.forEach(p => {
            out[p.sessionId] = p.username;
        });
        return out;
    }, [participants]);

    const zone = data?.zone;
    const fmtTime = ts => (ts == null ? "" : dayjs.tz(new Date(ts), zone).format("HH:mm:ss"));
    const fmtDateTime = ts => (ts == null ? "" : dayjs.tz(new Date(ts), zone).format("HH:mm DD/MM/YYYY"));
    const firstStart = included.length ? Math.min(...included.map(p => p.startTime)) : null;
    const lastEnd = included.length ? Math.max(...included.map(p => p.endTime)) : null;

    const getTooltipText = dot => tooltipText(dot, fmtTime(dot.timestamp), usernameBySession[dot.sessionId] || "");

    const validForMetric = sessionId =>
        result ? result.samples.filter(s => s.sessionId === sessionId && !s.rejected).length : 0;

    const statusLabel = p => {
        switch (p.status) {
            case "INCLUDED":
                return t("groupAirQualityIncluded", "inclusa");
            case "NO_ENV_DATA":
                return t("groupAirQualityNoEnvData", "senza dati ambientali");
            case "NO_TRACK":
                return t("groupAirQualityNoTrackStatus", "senza tracciato");
            default:
                return p.status;
        }
    };

    const pageError = pageErrorMessage(t, { daysError, daysStatus, days, error, data, participants, availableMetrics });

    const loading = daysStatus === "loading" || (day && status === "loading");

    // Le misure di tutte le grandezze con l'esito dei filtri e, per i campioni presi in un buco
    // GPS, la posizione stimata: e' gia' nella risposta, non serve un'altra richiesta.
    const downloadCsv = () => {
        new CsvBuilder(groupAirQualityCsvFileName(id, data.day, data.from, data.to))
            .setDelimeter(";")
            .setColumns(GROUP_AIR_QUALITY_CSV_COLUMNS)
            .addRows(buildGroupAirQualityCsvRows(results, zone, usernameBySession))
            .exportFile();
    };

    return (
        <Paper className={classes.page}>
            <Grid container justifyContent="space-between" alignItems="center" className={classes.container}>
                <Grid item>
                    <Typography variant="h6">{t("groupAirQuality", "Qualità dell'aria di gruppo")}</Typography>
                </Grid>
                <Grid item>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel id="group-air-quality-day">{t("groupAirQualityDay", "Giorno")}</InputLabel>
                                <Select
                                    labelId="group-air-quality-day"
                                    label={t("groupAirQualityDay", "Giorno")}
                                    value={days.some(d => d.day === day) ? day : ""}
                                    onChange={e => setQuery({ day: e.target.value, from: null, to: null })}
                                >
                                    {days.map(d => (
                                        <MenuItem key={d.day} value={d.day}>
                                            {dayjs(d.day).format("DD/MM/YYYY")} ({d.sessions})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item>
                            <TimeField label={t("groupAirQualityFrom", "Dalle")} value={from} onApply={v => setQuery({ from: v })} />
                        </Grid>
                        <Grid item>
                            <TimeField label={t("groupAirQualityTo", "Alle")} value={to} onApply={v => setQuery({ to: v })} />
                        </Grid>
                        <Grid item>
                            <Button
                                color="primary"
                                startIcon={<DownloadIcon />}
                                disabled={!data || !!pageError}
                                onClick={downloadCsv}
                            >
                                {t("exportCSV")}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button color="primary" startIcon={<HelpOutlineIcon />} onClick={() => setHelpOpen(true)}>
                                {t("groupAirQualityHelp", "Come leggere la mappa")}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button color="primary" startIcon={<InfoOutlinedIcon />} onClick={() => setLegendOpen(true)}>
                                {t("groupAirQualityCsvLegend", "Legenda CSV")}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {data && (
                <Grid container alignItems="flex-end" className={classes.container}>
                    <TextItem
                        value={`${dayjs(data.day).format("DD/MM/YYYY")} ${data.from}-${data.to} (${data.zone})`}
                        label={t("groupAirQualityDay", "Giorno")}
                        md={3}
                    />
                    <TextItem
                        value={`${included.length} / ${participants.length} / ${dayInfo ? dayInfo.sessions : "-"}`}
                        label={t("groupAirQualityParticipants", "Sessioni incluse / nella finestra / nel giorno")}
                        md={3}
                    />
                    <TextItem value={fmtDateTime(firstStart)} label={t("groupAirQualityFirstStart", "Prima partenza")} md={3} />
                    <TextItem value={fmtDateTime(lastEnd)} label={t("groupAirQualityLastEnd", "Ultimo arrivo")} md={3} />
                </Grid>
            )}

            {loading && (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress />
                </Box>
            )}

            {pageError && (
                <Alert severity="error" variant="outlined" sx={{ my: 2 }}>
                    {pageError}
                </Alert>
            )}

            {data && participants.length > 0 && (
                <Table size="small" className={classes.container}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("username")}</TableCell>
                            <TableCell>{t("startDate")}</TableCell>
                            <TableCell>{t("endDate")}</TableCell>
                            <TableCell align="right">{t("gpsDistance")}</TableCell>
                            <TableCell align="right">oaq</TableCell>
                            <TableCell align="right">pmx</TableCell>
                            <TableCell align="right">{t("groupAirQualityValidSamples", "Validi")} ({metricLabel})</TableCell>
                            <TableCell>{t("status")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {participants.map(p => (
                            <TableRow key={p.sessionId} sx={{ opacity: p.status === "INCLUDED" ? 1 : 0.6 }}>
                                <TableCell>
                                    <Link href={`/sessions/${p.sessionId}/environment`} target="_blank" underline="hover">
                                        {p.username}
                                    </Link>
                                </TableCell>
                                <TableCell>{fmtTime(p.startTime)}</TableCell>
                                <TableCell>{fmtTime(p.endTime)}</TableCell>
                                <TableCell align="right">{p.gpsDistance != null ? `${p.gpsDistance} Km` : ""}</TableCell>
                                <TableCell align="right">{p.oaqSamples}</TableCell>
                                <TableCell align="right">{p.pmxSamples}</TableCell>
                                <TableCell align="right">{validForMetric(p.sessionId)}</TableCell>
                                <TableCell>{statusLabel(p)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {data && !pageError && (
                <GroupAirQualityMap
                    pieces={data.pieces}
                    start={data.start}
                    end={data.end}
                    aggregation={aggregation}
                    getTooltipText={getTooltipText}
                />
            )}

            <GroupAirQualityCsvLegend open={legendOpen} onClose={() => setLegendOpen(false)} />
            <GroupAirQualityMapHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
        </Paper>
    );
}
